<template>
    <div class="ai-models">
        <!-- OpenAI RAG modu -->
        <template v-if="ragMethod !== 'supsis-rag'">
            <div class="header">
                <h3 class="title">{{ $t('chatbotv2.aiModelsComparison') }}</h3>
                <div class="actions">
                    <div class="control">
                        <label>{{ $t('chatbotv2.aiModelsChangeModel') }}</label>
                        <a-select :value="selectedModel" @change="onChangeSelectedModel" style="width: 220px;">
                            <a-select-option
                                v-for="m in models"
                                :key="m.id"
                                :value="m.id"
                            >{{ m.label }}</a-select-option>
                        </a-select>
                    </div>
                    <a-button type="primary" @click="saveSelectedModel" :loading="saving">{{ $t('common.save') }}</a-button>
                    <div class="legend">
                        <span class="badge best">{{ $t('chatbotv2.aiModelsRecommended') }}</span>
                        <span class="badge cheap">{{ $t('chatbotv2.aiModelsEconomic') }}</span>
                        <span class="badge codex">{{ $t('chatbotv2.aiModelsCodex') }}</span>
                    </div>
                </div>
            </div>

            <div class="cards" :class="{ 'selection-active': !!selectedModel }">
                <div
                    v-for="m in models"
                    :key="m.id"
                    class="card"
                    :class="[
                        { 'is-selected': selectedModel === m.id },
                        m.badge ? m.badge : '',
                        'model-' + m.cssClass
                    ]"
                    @click="onSelect(m.id)"
                >
                    <div v-if="selectedModel === m.id" class="selected-ribbon">
                        <i class="material-icons">check_circle</i>
                        <span>{{ $t("chatbotv2.selected") }}</span>
                    </div>
                    <div class="card-head">
                        <div class="name">{{ m.label }}</div>
                        <div class="right" v-if="m.badge">
                            <span class="badge" :class="m.badge">{{ badgeLabel(m.badge) }}</span>
                        </div>
                    </div>
                    <div class="banner">
                        <div class="banner-title">{{ m.label }}</div>
                    </div>
                    <div class="desc" v-if="m.desc">{{ m.desc }}</div>
                    <div class="details">
                        <div class="row"><span class="title">{{ $t("chatbotv2.intelligence") }}</span><span class="dots">{{ dots(m.intelligence) }}</span></div>
                        <div class="row"><span class="title">{{ $t("chatbotv2.speed") }}</span><span class="dots">{{ speedDots(m.speed) }}</span></div>
                        <div class="row"><span class="title">{{ $t("chatbotv2.input") }}</span><span class="icons"><span class="md-icon">text_fields</span><span class="md-icon" :class="{ disabled: !m.inputImage }">{{ m.inputImage ? 'image' : 'not_interested' }}</span><span class="md-icon disabled">not_interested</span></span></div>
                        <div class="row"><span class="title">{{ $t("chatbotv2.output") }}</span><span class="icons"><span class="md-icon">text_fields</span><span class="md-icon" :class="{ disabled: !m.outputImage }">{{ m.outputImage ? 'image' : 'not_interested' }}</span><span class="md-icon disabled">not_interested</span></span></div>
                        <div class="row"><span class="title">{{ $t("chatbotv2.reasoningTokens") }}</span><span class="icons">{{ m.reasoning ? '✔️' : '✖️' }}</span></div>
                        <div class="pricing-block">
                            <div class="block-title">{{ $t("chatbotv2.pricing") }} <span class="muted">{{ $t("chatbotv2.per1mTokens") }}</span></div>
                            <div class="p-row"><span>{{ $t("chatbotv2.input") }}</span><span>{{ m.priceInput || '—' }}</span></div>
                            <div class="p-row"><span>{{ $t("chatbotv2.cachedInput") }}</span><span>{{ m.priceCached || '—' }}</span></div>
                            <div class="p-row"><span>{{ $t("chatbotv2.output") }}</span><span>{{ m.priceOutput || '—' }}</span></div>
                        </div>
                        <div class="context-block">
                            <div class="block-title">{{ $t("chatbotv2.context") }}</div>
                            <div class="p-row"><span>{{ $t("chatbotv2.window") }}</span><span>{{ m.contextWindow || '—' }}</span></div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="disclaimer">{{ $t("chatbotv2.modelsDisclaimer") }}</div>
        </template>

        <!-- Supsis RAG modu: OpenRouter model listesi -->
        <template v-else>
            <div class="or-page">
                <!-- Top bar -->
                <div class="or-topbar">
                    <div class="or-topbar-left">
                        <h3 class="title">{{ $t("chatbotv2.llmModelOpenRouter") }}</h3>
                        <div class="or-selected-badge" v-if="autoRouterEnabled">
                            <span class="selected-label">{{ $t("chatbotv2.activeModel") }}:</span>
                            <b class="selected-model-name">openrouter/auto</b>
                            <a-tag color="cyan" style="margin-left:4px;font-size:11px">Auto Router</a-tag>
                        </div>
                        <div class="or-selected-badge" v-else-if="selectedORModel">
                            <span class="selected-label">{{ $t("chatbotv2.selected") }}:</span>
                            <b class="selected-model-name">{{ selectedORModel }}</b>
                            <span v-if="selectedORModelHasToolsWarning" class="tools-warn-inline">
                                <i class="material-icons">warning</i> {{ $t("chatbotv2.toolsNotSupported") }}
                            </span>
                        </div>
                    </div>
                    <div class="or-topbar-right">
                        <a-button
                            type="primary"
                            @click="saveORModel"
                            :loading="saving"
                            :disabled="!selectedORModel || autoRouterEnabled"
                        >{{ $t("message.save") }}</a-button>
                    </div>
                </div>

                <!-- Auto Router aktif uyarısı -->
                <div v-if="autoRouterEnabled" style="margin-bottom:10px;">
                    <a-alert type="info" show-icon>
                        <template slot="message">
                            <b>Auto Router Aktif</b> — Model seçimi devre dışı
                        </template>
                        <template slot="description">
                            <b>Plugins &gt; Auto Router</b> açık olduğu için primary model olarak <code>openrouter/auto</code> kullanılmaktadır.
                            Her istek için en uygun model NotDiamond tarafından otomatik seçilir.
                            Model değiştirmek istiyorsanız önce <b>Plugins</b> sayfasından Auto Router'ı kapatın.
                        </template>
                    </a-alert>
                </div>

                <!-- Uyarılar -->
                <div v-if="orError" style="margin-bottom:10px;">
                    <a-alert type="error" :message="orError" show-icon />
                </div>
                <div v-if="chatbotHasTools && selectedORModel && selectedORModelHasToolsWarning" style="margin-bottom:10px;">
                    <a-alert type="warning" show-icon>
                        <template slot="message">
                            Seçili model <b>tools</b> parametresini desteklemiyor — chatbot'unuzda fonksiyon/araç aktif.
                            Aşağıdan <b>tools destekleyen</b> bir fallback model seçin.
                        </template>
                    </a-alert>
                </div>
                <div v-if="isByokChannel" style="margin-bottom:10px;">
                    <a-alert type="warning" show-icon message="BYOK Mod Aktif — Fiyatlara %10 Supsis hizmet ücreti eklenir." />
                </div>

                <!-- Routing + Fallback config -->
                <div class="or-routing-config">
                    <div class="config-row">
                        <label>Yönlendirme:</label>
                        <a-radio-group v-model="orRoutingStrategy" size="small">
                            <a-radio-button value="default">Fiyat Dengeli</a-radio-button>
                            <a-radio-button value="price">En Ucuz</a-radio-button>
                            <a-radio-button value="throughput">En Hızlı</a-radio-button>
                            <a-radio-button value="latency">En Düşük Gecikme</a-radio-button>
                        </a-radio-group>
                    </div>
                    <div class="config-row" style="margin-top:8px;flex-wrap:wrap;gap:8px;">
                        <label>Fallback:</label>
                        <a-select
                            mode="multiple"
                            v-model="selectedORFallbacks"
                            placeholder="Primary model hata verirse kullanılacak modeller..."
                            style="min-width:380px;"
                            :filter-option="orFallbackFilter"
                        >
                            <a-select-option v-for="m in filteredORModels.slice(0, 100)" :key="m.id" :value="m.id">
                                {{ m.id }}
                            </a-select-option>
                        </a-select>
                        <span
                            v-if="chatbotHasTools && selectedORFallbacks.length === 0 && selectedORModelHasToolsWarning"
                            class="inline-tools-warn"
                        >
                            <i class="material-icons">warning</i> Tools destekleyen bir fallback model gerekli
                        </span>
                    </div>
                </div>

                <!-- Provider Strip: tam genişlik, yatay scroll, arama çubuğunun üstünde -->
                <div class="or-provider-strip" v-if="enrichedProviders.length > 0">
                    <div class="strip-header">
                        <span class="strip-label">Provider</span>
                        <span
                            v-if="filterProviders.length > 0"
                            class="strip-clear"
                            @click.stop="clearProviderFilter"
                        >Temizle ({{ filterProviders.length }})</span>
                    </div>
                    <div class="strip-scroll-wrap">
                        <div class="strip-scroll">
                            <a-tooltip
                                v-for="prov in enrichedProviders"
                                :key="prov.slug"
                                :title="prov.displayName"
                                placement="bottom"
                            >
                                <div
                                    class="strip-chip"
                                    :class="{ active: filterProviders.indexOf(prov.slug) !== -1 }"
                                    @click="toggleProvider(prov.slug)"
                                >
                                    <img
                                        v-if="prov.icon"
                                        :src="prov.icon"
                                        :alt="prov.displayName"
                                        class="strip-icon"
                                        @error="onProviderIconError($event)"
                                    />
                                    <span v-else class="strip-fallback">{{ prov.displayName.slice(0, 2).toUpperCase() }}</span>
                                    <span class="strip-name">{{ prov.displayName }}</span>
                                </div>
                            </a-tooltip>
                        </div>
                    </div>
                </div>

                <!-- Loading -->
                <div v-if="orLoading" class="or-loading">
                    <a-spin tip="Modeller yükleniyor..." />
                </div>

                <!-- Main layout: sidebar + grid -->
                <div v-else class="or-main-layout">
                    <!-- Sidebar Filters -->
                    <div class="or-sidebar" :class="{ 'is-closed': !sidebarOpen }">
                        <div class="or-sidebar-inner">

                        <!-- Arama -->
                        <div class="sidebar-search">
                            <a-input
                                v-model="orSearchQuery"
                                placeholder="Model ara..."
                                allow-clear
                                @change="filterORModels"
                            >
                                <i class="material-icons" slot="prefix" style="font-size:16px;color:#bbb;line-height:1;">search</i>
                            </a-input>
                        </div>

                        <!-- Input Modalities -->
                        <div class="filter-section">
                            <div class="filter-section-header" @click="toggleSection('input')">
                                <span class="section-title-wrap">
                                    <i class="material-icons section-icon">input</i>
                                    <span>Input Modalities</span>
                                    <span class="section-badge" v-if="filterInputModalities.length > 0">{{ filterInputModalities.length }}</span>
                                </span>
                                <i class="material-icons section-chevron" :class="{ open: openSections.input }">expand_more</i>
                            </div>
                            <div class="filter-section-body" v-show="openSections.input">
                                <div class="modality-mandatory-row">
                                    <a-tooltip title="Tüm modeller metin girişi destekler. Chatbot'un çalışması için zorunludur." placement="right">
                                        <span class="mod-chip active"><i class="material-icons">text_fields</i> Text</span>
                                    </a-tooltip>
                                    <span class="mandatory-tag">Zorunlu</span>
                                </div>
                                <a-tooltip
                                    v-for="mod in inputModalityOptions"
                                    :key="mod.value"
                                    :title="mod.tooltip"
                                    placement="right"
                                >
                                    <label
                                        class="modality-row"
                                        :class="{ active: filterInputModalities.indexOf(mod.value) !== -1 }"
                                        @click="toggleInputModality(mod.value)"
                                    >
                                        <i class="material-icons mod-row-icon">{{ mod.icon }}</i>
                                        <span class="mod-row-label">{{ mod.label }}</span>
                                        <i class="material-icons mod-row-check" v-if="filterInputModalities.indexOf(mod.value) !== -1">check</i>
                                    </label>
                                </a-tooltip>
                                <div class="filter-hint">
                                    <i class="material-icons">lightbulb</i>
                                    Chatbot için <b>image + file</b> destekleyen modeller önerilir
                                </div>
                            </div>
                        </div>

                        <!-- Output Modalities -->
                        <div class="filter-section">
                            <div class="filter-section-header" @click="toggleSection('output')">
                                <span class="section-title-wrap">
                                    <i class="material-icons section-icon">output</i>
                                    <span>Output Modalities</span>
                                    <span class="section-badge" v-if="filterOutputModalities.length > 0">{{ filterOutputModalities.length }}</span>
                                </span>
                                <i class="material-icons section-chevron" :class="{ open: openSections.output }">expand_more</i>
                            </div>
                            <div class="filter-section-body" v-show="openSections.output">
                                <a-tooltip
                                    v-for="mod in outputModalityOptions"
                                    :key="mod.value"
                                    :title="mod.tooltip"
                                    placement="right"
                                >
                                    <label
                                        class="modality-row"
                                        :class="{ active: filterOutputModalities.indexOf(mod.value) !== -1 }"
                                        @click="toggleOutputModality(mod.value)"
                                    >
                                        <i class="material-icons mod-row-icon">{{ mod.icon }}</i>
                                        <span class="mod-row-label">{{ mod.label }}</span>
                                        <i class="material-icons mod-row-check" v-if="filterOutputModalities.indexOf(mod.value) !== -1">check</i>
                                    </label>
                                </a-tooltip>
                            </div>
                        </div>

                        <!-- Supported Parameters -->
                        <div class="filter-section">
                            <div class="filter-section-header" @click="toggleSection('params')">
                                <span class="section-title-wrap">
                                    <i class="material-icons section-icon">settings</i>
                                    <span>Supported Parameters</span>
                                    <span class="section-badge" v-if="filterSupportedParams.length > 0">{{ filterSupportedParams.length }}</span>
                                </span>
                                <i class="material-icons section-chevron" :class="{ open: openSections.params }">expand_more</i>
                            </div>
                            <div class="filter-section-body" v-show="openSections.params">
                                <a-tooltip
                                    v-for="param in supportedParamOptions"
                                    :key="param.value"
                                    :title="param.tooltip"
                                    placement="right"
                                >
                                    <label
                                        class="modality-row"
                                        :class="{
                                            active: filterSupportedParams.indexOf(param.value) !== -1,
                                            'row-tools': param.value === 'tools' && chatbotHasTools
                                        }"
                                        @click="toggleSupportedParam(param.value)"
                                    >
                                        <i class="material-icons mod-row-icon">{{ param.icon }}</i>
                                        <span class="mod-row-label">
                                            {{ param.label }}
                                            <span class="tools-required-dot" v-if="param.value === 'tools' && chatbotHasTools">●</span>
                                        </span>
                                        <i class="material-icons mod-row-check" v-if="filterSupportedParams.indexOf(param.value) !== -1">check</i>
                                    </label>
                                </a-tooltip>
                                <div class="filter-hint tools-hint" v-if="chatbotHasTools">
                                    <i class="material-icons">warning</i>
                                    Chatbot fonksiyon kullanıyor — <b>tools</b> zorunlu
                                </div>
                            </div>
                        </div>

                        <!-- Context Window -->
                        <div class="filter-section">
                            <div class="filter-section-header" @click="toggleSection('context')">
                                <span class="section-title-wrap">
                                    <i class="material-icons section-icon">straighten</i>
                                    <span>Context Window</span>
                                    <span class="section-badge" v-if="filterContextIdx > 0">min</span>
                                </span>
                                <i class="material-icons section-chevron" :class="{ open: openSections.context }">expand_more</i>
                            </div>
                            <div class="filter-section-body" v-show="openSections.context">
                                <div class="section-desc">Minimum context penceresi. Uzun belge veya konuşma geçmişi için yüksek context gereklidir.</div>
                                <div class="slider-wrap">
                                    <a-slider
                                        v-model="filterContextIdx"
                                        :min="0"
                                        :max="9"
                                        :marks="contextMarks"
                                        :step="null"
                                        @change="filterORModels"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Pricing -->
                        <div class="filter-section">
                            <div class="filter-section-header" @click="toggleSection('pricing')">
                                <span class="section-title-wrap">
                                    <i class="material-icons section-icon">attach_money</i>
                                    <span>Pricing</span>
                                    <span class="section-badge" v-if="filterPricingIdx > 0">max</span>
                                </span>
                                <i class="material-icons section-chevron" :class="{ open: openSections.pricing }">expand_more</i>
                            </div>
                            <div class="filter-section-body" v-show="openSections.pricing">
                                <div class="section-desc">Maksimum input fiyatı ($/1M token). Free = ücretsiz modeller.</div>
                                <div class="slider-wrap">
                                    <a-slider
                                        v-model="filterPricingIdx"
                                        :min="0"
                                        :max="7"
                                        :marks="pricingMarks"
                                        :step="null"
                                        @change="filterORModels"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Filtreleri temizle -->
                        <div class="sidebar-clear-btn" v-if="hasActiveFilters">
                            <a-button block size="small" @click="clearAllFilters">
                                Tüm Filtreleri Temizle
                            </a-button>
                        </div>
                        </div><!-- /or-sidebar-inner -->
                    </div>

                    <!-- Model Cards Area -->
                    <div class="or-model-area">
                        <div class="or-model-area-header">
                            <div class="model-area-left">
                                <button class="sidebar-toggle-btn" @click="toggleSidebar" :title="sidebarOpen ? 'Filtreleri gizle' : 'Filtreleri göster'">
                                    <i class="material-icons">{{ sidebarOpen ? 'chevron_left' : 'tune' }}</i>
                                    <span>{{ sidebarOpen ? 'Gizle' : 'Filtreler' }}</span>
                                    <span class="active-filter-dot" v-if="hasActiveFilters && !sidebarOpen"></span>
                                </button>
                                <span class="model-count">{{ filteredORModels.length }} model</span>
                            </div>
                            <div class="recommendation-hint">
                                <span class="rec-dot"></span>
                                <b>Chatbot için önerilir:</b> text + image + file destekleyen modeller
                            </div>
                        </div>

                        <div class="or-cards" :class="{ 'auto-router-locked': autoRouterEnabled }">
                            <div
                                v-for="m in paginatedORModels"
                                :key="m.id"
                                class="or-card"
                                :class="{
                                    'is-selected': selectedORModel === m.id,
                                    'is-fallback': selectedORFallbacks.indexOf(m.id) !== -1,
                                    'is-recommended': isRecommendedForChatbot(m),
                                    'has-tools-issue': chatbotHasTools && !modelSupportsParam(m, 'tools'),
                                    'is-locked': autoRouterEnabled
                                }"
                                @click="autoRouterEnabled ? null : selectORModel(m.id)"
                            >
                                <!-- Selected ribbon -->
                                <div class="selected-ribbon" v-if="selectedORModel === m.id">
                                    <i class="material-icons">check_circle</i> Primary
                                </div>
                                <div class="selected-ribbon fallback-ribbon" v-else-if="selectedORFallbacks.indexOf(m.id) !== -1">
                                    <i class="material-icons">account_tree</i> Fallback
                                </div>

                                <!-- Top badge -->
                                <div class="card-top-badge recommended-badge" v-if="isRecommendedForChatbot(m)">
                                    <i class="material-icons">star</i> Chatbot için önerilir
                                </div>
                                <div class="card-top-badge tools-badge" v-else-if="chatbotHasTools && !modelSupportsParam(m, 'tools')">
                                    <i class="material-icons">warning</i> Tools yok
                                </div>

                                <!-- Provider -->
                                <div class="or-card-provider">
                                    <img
                                        v-if="getEnrichedProviderIcon(m.id)"
                                        :src="getEnrichedProviderIcon(m.id)"
                                        class="or-prov-img"
                                        :alt="getProvider(m.id)"
                                        @error="onProviderIconError($event)"
                                    />
                                    <span class="or-prov-name">{{ getEnrichedProviderName(m.id) }}</span>
                                </div>

                                <!-- Name & ID -->
                                <div class="or-model-name">{{ m.name || m.id }}</div>
                                <div class="or-model-id">{{ m.id }}</div>

                                <!-- Modalities -->
                                <div class="or-modalities">
                                    <div class="mod-row-group">
                                        <span class="mod-group-lbl">In</span>
                                        <a-tooltip
                                            v-for="mod in getInputModalities(m)"
                                            :key="'in-' + mod"
                                            :title="getModalityTooltip('input', mod)"
                                            placement="top"
                                        >
                                            <span class="mod-badge" :class="'mod-' + mod">{{ mod }}</span>
                                        </a-tooltip>
                                    </div>
                                    <div class="mod-row-group">
                                        <span class="mod-group-lbl">Out</span>
                                        <a-tooltip
                                            v-for="mod in getOutputModalities(m)"
                                            :key="'out-' + mod"
                                            :title="getModalityTooltip('output', mod)"
                                            placement="top"
                                        >
                                            <span class="mod-badge out" :class="'mod-' + mod">{{ mod }}</span>
                                        </a-tooltip>
                                    </div>
                                </div>

                                <!-- Supported params chips -->
                                <div class="or-params-row">
                                    <a-tooltip
                                        v-for="param in getKeyParams(m)"
                                        :key="param"
                                        :title="getParamTooltip(param)"
                                        placement="top"
                                    >
                                        <span
                                            class="param-chip"
                                            :class="{
                                                'chip-tools': param === 'tools',
                                                'chip-tools-warn': param === 'tools' && chatbotHasTools
                                            }"
                                        >{{ param }}</span>
                                    </a-tooltip>
                                </div>

                                <!-- Stats -->
                                <div class="or-stats">
                                    <a-tooltip title="Bu modelin işleyebileceği maksimum token sayısı" placement="top">
                                        <div class="or-stat" v-if="m.context_length">
                                            <span class="stat-lbl">Context</span>
                                            <span class="stat-val">{{ formatContextLength(m.context_length) }}</span>
                                        </div>
                                    </a-tooltip>
                                    <a-tooltip title="Her 1 milyon input token başına ücret" placement="top">
                                        <div class="or-stat">
                                            <span class="stat-lbl">In/1M</span>
                                            <span class="stat-val">{{ formatPrice(m, 'prompt') }}</span>
                                        </div>
                                    </a-tooltip>
                                    <a-tooltip title="Her 1 milyon output token başına ücret" placement="top">
                                        <div class="or-stat">
                                            <span class="stat-lbl">Out/1M</span>
                                            <span class="stat-val">{{ formatPrice(m, 'completion') }}</span>
                                        </div>
                                    </a-tooltip>
                                </div>
                            </div>
                        </div>

                        <div class="or-pagination" v-if="filteredORModels.length > orPageSize">
                            <a-pagination
                                :current="orCurrentPage"
                                :total="filteredORModels.length"
                                :page-size="orPageSize"
                                @change="onORPageChange"
                                size="small"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
<script>
import ALL_PROVIDERS from "./all_providers.json";
import { CHATBOT_API } from "../../../../api/chatbotv2";
import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";

// OpenAI API model listesi
const MODELS = [
    { id: "gpt-5-mini", label: "GPT-5 mini", desc: "Faster, cost-efficient version of GPT-5 for well-defined tasks", badge: "cheap", cssClass: "gpt-5-mini", intelligence: 4, speed: 4, inputImage: true, outputImage: false, reasoning: true, priceInput: "$0.25", priceCached: "—", priceOutput: "—", contextWindow: "400,000" },
    { id: "gpt-5-nano", label: "GPT-5 nano", desc: "Fastest, most cost-efficient version of GPT-5", badge: "cheap", cssClass: "gpt-5-nano", intelligence: 3, speed: 5, inputImage: true, outputImage: false, reasoning: true, priceInput: "—", priceCached: "—", priceOutput: "—", contextWindow: "—" },
    { id: "gpt-5.1", label: "GPT-5.1", desc: "Best model for coding and agentic tasks with configurable reasoning effort", cssClass: "gpt-51", intelligence: 5, speed: 3, inputImage: true, outputImage: false, reasoning: true, priceInput: "—", priceCached: "—", priceOutput: "—", contextWindow: "400,000" },
    { id: "gpt-5.2-pro", label: "GPT-5.2 pro", desc: "Version of GPT-5.2 that produces smarter and more precise responses", cssClass: "gpt-52-pro", intelligence: 5, speed: 3, inputImage: true, outputImage: false, reasoning: true, priceInput: "—", priceCached: "—", priceOutput: "—", contextWindow: "—" },
    { id: "gpt-5.2", label: "GPT-5.2", desc: "Best model for coding and agentic tasks across industries", badge: "best", cssClass: "gpt-52", intelligence: 5, speed: 3, inputImage: true, outputImage: false, reasoning: true, priceInput: "$1.75", priceCached: "$0.175", priceOutput: "$14.00", contextWindow: "400,000" },
    { id: "gpt-5", label: "GPT-5", desc: "Previous intelligent reasoning model for coding and agentic tasks", cssClass: "gpt-5", intelligence: 5, speed: 3, inputImage: true, outputImage: false, reasoning: true, priceInput: "$1.25", priceCached: "—", priceOutput: "$10.00", contextWindow: "400,000" },
    { id: "gpt-4.1", label: "GPT-4.1", desc: "Smartest non-reasoning model", cssClass: "gpt-41", intelligence: 4, speed: 3, inputImage: true, outputImage: false, reasoning: false, priceInput: "$2.00", priceCached: "$0.50", priceOutput: "$8.00", contextWindow: "1,047,576" },
];

// Fallback provider icon map (model ID prefix -> Google favicon URL)
const PROVIDER_ICON_MAP = {
    "openai": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://openai.com&size=64",
    "anthropic": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://www.anthropic.com&size=64",
    "google": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://google.com&size=64",
    "meta-llama": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://meta.com&size=64",
    "mistralai": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://mistral.ai&size=64",
    "qwen": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://qwenlm.github.io&size=64",
    "deepseek": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://deepseek.com&size=64",
    "x-ai": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://x.ai&size=64",
    "cohere": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://cohere.com&size=64",
    "nvidia": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://www.nvidia.com&size=64",
    "microsoft": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://microsoft.com&size=64",
    "perplexity": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://perplexity.ai&size=64",
    "together": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://together.ai&size=64",
    "deepinfra": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://deepinfra.com&size=64",
    "sambanova": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://sambanova.ai&size=64",
    "cerebras": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://www.cerebras.ai&size=64",
    "moonshotai": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://moonshot.ai&size=64",
    "liquid": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://www.liquid.ai&size=64",
    "minimax": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://www.minimax.io&size=64",
    "nousresearch": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://nousresearch.com&size=64",
    "groq": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://groq.com&size=64",
    "fireworks": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://fireworks.ai&size=64",
    "siliconflow": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://siliconflow.com&size=64",
    "neversleep": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://neversleep.ai&size=64",
    "inception": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://www.inceptionlabs.ai&size=64",
    "featherless": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://featherless.ai&size=64",
    "hyperbolic": "https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://hyperbolic.xyz&size=64",
};

// Context slider steps (index -> min context tokens)
var CONTEXT_STEPS = [0, 4096, 8192, 16384, 32768, 65536, 131072, 262144, 524288, 1048576];
var CONTEXT_MARKS = { 0: "Any", 1: "4K", 2: "8K", 3: "16K", 4: "32K", 5: "64K", 6: "128K", 7: "256K", 8: "512K", 9: "1M+" };

// Pricing slider steps (index -> max input price per 1M tokens; Infinity = no limit)
var PRICE_STEPS = [Infinity, 0, 0.1, 0.25, 0.5, 1, 2, 5];
var PRICE_MARKS = { 0: "Any", 1: "Free", 2: "$0.10", 3: "$0.25", 4: "$0.50", 5: "$1", 6: "$2", 7: "$5" };

// Key parameters to highlight on cards
var KEY_PARAMS = ["tools", "temperature", "top_p", "top_k", "reasoning", "stream"];

// Modality tooltip metinleri
var MODALITY_TOOLTIPS = {
    input_text: "Metin girişi — tüm modeller için temel gereklilik.",
    input_image: "Görüntü/fotoğraf anlama. Chatbot'a gönderilen görseller işlenecekse gereklidir.",
    input_file: "PDF, Word, Excel gibi dosya içeriğini okuma. Bilgi tabanı veya müşteri dosya paylaşımı için gereklidir.",
    input_audio: "Ses kaydı veya konuşma anlama. Chatbot sesli mesaj işliyorsa gereklidir.",
    output_text: "Model metin yanıtı üretir. Tüm chatbot'lar için temel gereklilik.",
    output_image: "Model görüntü oluşturabilir. Chatbot görsel üretecekse gereklidir.",
    output_audio: "Model sesli yanıt üretebilir. Sesli chatbot kurulumu için gereklidir.",
};

// Supported parameter tooltip metinleri
var PARAM_TOOLTIPS = {
    tools: "AI fonksiyonları (transfer_chat, close_chat, show_option_buttons vb.) ve özel AI function'ları kullanmak için zorunlu parametredir. Chatbot'unuzda herhangi bir araç/fonksiyon aktifse bu parametreyi destekleyen model seçilmelidir.",
    temperature: "Yanıt yaratıcılığını kontrol eder. Düşük değer (0-0.3) tutarlı/öngörülebilir, yüksek değer (0.7-1.0) yaratıcı/çeşitli yanıtlar üretir.",
    top_p: "Olasılık kütlesi üzerinden sözcük çeşitliliği kontrolü. Temperature ile birlikte yanıt kalitesini ayarlar.",
    top_k: "Her adımda değerlendirilen en yüksek olasılıklı token sayısını sınırlar.",
    reasoning: "Model adım adım iç düşünce zinciri (chain-of-thought) oluşturabilir. Karmaşık mantık ve analiz sorularında daha iyi sonuçlar verir.",
    stream: "Yanıtı token token akış halinde (streaming) alabilme. Uzun yanıtlarda daha hızlı ilk token süresi sağlar.",
};

export default {
    name: "AIModels",
    props: {
        storyId: String,
    },
    data() {
        var apiModelMap = {};
        MODELS.forEach(function(m) {
            apiModelMap[m.id] = m.id;
        });
        return {
            selectedModel: "gpt-5.2",
            saving: false,
            models: MODELS,
            apiModelMap: apiModelMap,

            // Supsis RAG / OpenRouter
            ragMethod: "openai-rag",
            orModels: [],
            orLoading: false,
            orError: null,
            orSearchQuery: "",
            orCurrentPage: 1,
            orPageSize: 12,
            selectedORModel: null,
            selectedORFallbacks: [],
            orRoutingStrategy: "default",
            isByokChannel: false,
            orSubscription: null,

            // OR providers listesi (all_providers.json'dan)
            orAllProviders: ALL_PROVIDERS.data || [],

            // Chatbot context
            chatbotHasTools: false,
            autoRouterEnabled: false,

            // Filter state
            filterInputModalities: [],
            filterOutputModalities: [],
            filterSupportedParams: [],
            filterContextIdx: 0,
            filterPricingIdx: 0,
            filterProviders: [],

            // Sidebar açık/kapalı
            sidebarOpen: true,
            sidebarAutoCloseTimer: null,

            // Accordion açık/kapalı durumu
            openSections: {
                input: true,
                output: false,
                params: false,
                context: false,
                pricing: false,
            },

            // Slider marks
            contextMarks: CONTEXT_MARKS,
            pricingMarks: PRICE_MARKS,

            // Filter option definitions (tooltip dahil)
            inputModalityOptions: [
                { value: "image", label: "Image", icon: "image", tooltip: MODALITY_TOOLTIPS.input_image },
                { value: "file", label: "File", icon: "attach_file", tooltip: MODALITY_TOOLTIPS.input_file },
                { value: "audio", label: "Audio", icon: "mic", tooltip: MODALITY_TOOLTIPS.input_audio },
            ],
            outputModalityOptions: [
                { value: "text", label: "Text", icon: "text_fields", tooltip: MODALITY_TOOLTIPS.output_text },
                { value: "image", label: "Image", icon: "image", tooltip: MODALITY_TOOLTIPS.output_image },
                { value: "audio", label: "Audio", icon: "volume_up", tooltip: MODALITY_TOOLTIPS.output_audio },
            ],
            supportedParamOptions: [
                { value: "tools", label: "Tools", icon: "build", tooltip: PARAM_TOOLTIPS.tools },
                { value: "temperature", label: "Temperature", icon: "thermostat", tooltip: PARAM_TOOLTIPS.temperature },
                { value: "top_p", label: "Top P", icon: "tune", tooltip: PARAM_TOOLTIPS.top_p },
                { value: "reasoning", label: "Reasoning", icon: "psychology", tooltip: PARAM_TOOLTIPS.reasoning },
            ],
        };
    },
    computed: {
        currentStoryAi() {
            var storyId = this.storyId;
            var story = this.$store.state.CB.stories.find(function(s) { return s._id === storyId; });
            return story ? story.ai : null;
        },
        filteredORModels() {
            var self = this;
            var q = (this.orSearchQuery || "").toLowerCase();
            var filtered = this.orModels.filter(function(m) {
                if (q && m.id.toLowerCase().indexOf(q) === -1 && (m.name || "").toLowerCase().indexOf(q) === -1) {
                    return false;
                }
                if (self.filterProviders.length > 0) {
                    var prefix = m.id ? m.id.split("/")[0] : "";
                    if (self.filterProviders.indexOf(prefix) === -1) return false;
                }
                if (self.filterInputModalities.length > 0) {
                    var inputs = self.getInputModalities(m);
                    for (var i = 0; i < self.filterInputModalities.length; i++) {
                        if (inputs.indexOf(self.filterInputModalities[i]) === -1) return false;
                    }
                }
                if (self.filterOutputModalities.length > 0) {
                    var outputs = self.getOutputModalities(m);
                    for (var j = 0; j < self.filterOutputModalities.length; j++) {
                        if (outputs.indexOf(self.filterOutputModalities[j]) === -1) return false;
                    }
                }
                if (self.filterSupportedParams.length > 0) {
                    var params = m.supported_parameters || [];
                    for (var k = 0; k < self.filterSupportedParams.length; k++) {
                        if (params.indexOf(self.filterSupportedParams[k]) === -1) return false;
                    }
                }
                if (self.filterContextIdx > 0) {
                    var minCtx = CONTEXT_STEPS[self.filterContextIdx];
                    if (!m.context_length || m.context_length < minCtx) return false;
                }
                if (self.filterPricingIdx > 0) {
                    var maxPrice = PRICE_STEPS[self.filterPricingIdx];
                    var rawPrice = m.pricing && m.pricing.prompt ? parseFloat(m.pricing.prompt) : null;
                    if (rawPrice === null || isNaN(rawPrice)) return false;
                    var pricePerM = rawPrice * 1000000;
                    if (pricePerM > maxPrice) return false;
                }
                return true;
            });

            // Arama yoksa: primary + fallback modelleri en üste taşı
            if (!q) {
                var priorityIds = [];
                if (self.selectedORModel) priorityIds.push(self.selectedORModel);
                if (self.selectedORFallbacks) {
                    self.selectedORFallbacks.forEach(function(id) { priorityIds.push(id); });
                }
                if (priorityIds.length > 0) {
                    filtered = filtered.slice().sort(function(a, b) {
                        var ai = priorityIds.indexOf(a.id);
                        var bi = priorityIds.indexOf(b.id);
                        if (ai !== -1 && bi !== -1) return ai - bi;
                        if (ai !== -1) return -1;
                        if (bi !== -1) return 1;
                        return 0;
                    });
                }
            }

            return filtered;
        },
        paginatedORModels() {
            var start = (this.orCurrentPage - 1) * this.orPageSize;
            return this.filteredORModels.slice(start, start + this.orPageSize);
        },
        // Yüklenen modellerden elde edilen unique provider slug'ları
        orUniqueProviders() {
            var seen = {};
            var result = [];
            this.orModels.forEach(function(m) {
                var prefix = m.id ? m.id.split("/")[0] : "";
                if (prefix && !seen[prefix]) {
                    seen[prefix] = true;
                    result.push({ slug: prefix });
                }
            });
            return result;
        },
        // OR API'den gelen providerları model listesiyle merge eder
        enrichedProviders() {
            var apiMap = {};
            this.orAllProviders.forEach(function(p) {
                if (p.slug) apiMap[p.slug] = p;
            });
            return this.orUniqueProviders.map(function(p) {
                var apiData = apiMap[p.slug];
                if (apiData) {
                    return {
                        slug: p.slug,
                        displayName: apiData.displayName || p.slug,
                        icon: (apiData.icon && apiData.icon.url) ? apiData.icon.url : (PROVIDER_ICON_MAP[p.slug] || null),
                    };
                }
                return {
                    slug: p.slug,
                    displayName: p.slug,
                    icon: PROVIDER_ICON_MAP[p.slug] || null,
                };
            });
        },
        enrichedProviderMap() {
            var map = {};
            this.enrichedProviders.forEach(function(p) {
                map[p.slug] = p;
            });
            return map;
        },
        selectedORModelHasToolsWarning() {
            if (!this.selectedORModel || !this.chatbotHasTools) return false;
            var self = this;
            var m = this.orModels.find(function(x) { return x.id === self.selectedORModel; });
            if (!m) return false;
            return !this.modelSupportsParam(m, "tools");
        },
        hasActiveFilters() {
            return (
                this.filterInputModalities.length > 0 ||
                this.filterOutputModalities.length > 0 ||
                this.filterSupportedParams.length > 0 ||
                this.filterContextIdx > 0 ||
                this.filterPricingIdx > 0 ||
                this.filterProviders.length > 0 ||
                (this.orSearchQuery || "").length > 0
            );
        },
    },
    methods: {
        dots(n) {
            const count = Math.min(5, Math.max(0, Number(n) || 0));
            return "● ".repeat(count).trim() || "—";
        },
        speedDots(n) {
            const count = Math.min(5, Math.max(0, Number(n) || 0));
            return "⚡".repeat(count) || "—";
        },
        badgeLabel(badge) {
            const map = {
                best: this.$t("chatbotv2.aiModelsRecommended"),
                cheap: this.$t("chatbotv2.aiModelsEconomic"),
                codex: this.$t("chatbotv2.aiModelsCodex"),
            };
            return map[badge] || badge;
        },
        onChangeSelectedModel(val) {
            this.selectedModel = val;
        },
        onSelect(model) {
            this.selectedModel = model;
        },
        normalizeModelName(model) {
            if (!model) return null;
            if (model === "gpt-5-fast") return "gpt-5";
            const found = this.models.find((m) => m.id === model);
            return found ? model : model;
        },
        initSelectedModel() {
            try {
                const cacheKey = `ai.selectedModel.${this.storyId}`;
                const cached = typeof window !== "undefined" ? window.localStorage.getItem(cacheKey) : null;
                if (cached && this.models.some((m) => m.id === cached)) {
                    this.selectedModel = this.normalizeModelName(cached) || cached;
                    return;
                }
                const story = (this.$store && this.$store.state && this.$store.state.CB && Array.isArray(this.$store.state.CB.stories))
                    ? this.$store.state.CB.stories.find((s) => s._id === this.storyId)
                    : null;
                const current = story && story.ai && story.ai.model ? story.ai.model : null;
                this.selectedModel = this.normalizeModelName(current) || "gpt-5.2";
            } catch (e) {
                this.selectedModel = "gpt-5.2";
            }
        },
        loadStoryAISettings() {
            var self = this;
            var story = this.$store.state.CB.stories.find(function(s) { return s._id === self.storyId; });
            if (story && story.ai) {
                this.ragMethod = story.ai.ragMethod || "openai-rag";
                this.selectedORModel = story.ai.llmModel || null;
                this.selectedORFallbacks = story.ai.fallbackModels || [];
                this.orRoutingStrategy = story.ai.routingStrategy || "default";
                var enabledFunctions = story.ai.enabledFunctions || [];
                this.chatbotHasTools = enabledFunctions.length > 0;
                this.autoRouterEnabled = !!(story.ai.plugins && story.ai.plugins.autoRouter && story.ai.plugins.autoRouter.enabled);

                var channelId = story.ai.openrouterChannel;
                if (channelId) {
                    var channels = this.$store.state.channel.channels || [];
                    var ch = channels.find(function(c) { return c._id === channelId; });
                    this.isByokChannel = ch && ch.payload && ch.payload.keyMode === "byok";
                }
            }
            if (this.ragMethod === "supsis-rag") {
                this.loadORModels();
                this.startSidebarAutoClose();
            }
        },
        loadORModels() {
            var self = this;
            self.orLoading = true;
            self.orError = null;

            var story = this.$store.state.CB.stories.find(function(s) { return s._id === self.storyId; });
            var channelId = story && story.ai ? story.ai.openrouterChannel : null;

            if (!channelId) {
                self.orLoading = false;
                self.orError = "OpenRouter kanalı seçili değil. RAG Ayarları'ndan bir kanal seçin.";
                return;
            }

            var siteId = this.$store.state.site.site._id;
            var userId = this.$store.state.auth.user._id;

            sendSocketMessage("user openrouter-list-models", {
                siteId: siteId,
                userId: userId,
                channelId: channelId,
            });

            if (self.orSubscription) {
                self.orSubscription.unsubscribe();
            }
            self.orSubscription = socketMessageStream.subscribe(function(msg) {
                if (msg.cmd === "openrouter_list_models_result") {
                    self.orLoading = false;
                    if (msg.data && msg.data.success && Array.isArray(msg.data.models)) {
                        self.orModels = msg.data.models;
                    } else {
                        self.orError = (msg.data && msg.data.message) || "Model listesi alınamadı.";
                    }
                    if (self.orSubscription) {
                        self.orSubscription.unsubscribe();
                        self.orSubscription = null;
                    }
                }
            });
            setTimeout(function() {
                if (self.orLoading) {
                    self.orLoading = false;
                    self.orError = "Model listesi yüklenemedi. Lütfen tekrar deneyin.";
                }
            }, 15000);
        },
        toggleSidebar() {
            this.sidebarOpen = !this.sidebarOpen;
            if (this.sidebarAutoCloseTimer) {
                clearTimeout(this.sidebarAutoCloseTimer);
                this.sidebarAutoCloseTimer = null;
            }
        },
        startSidebarAutoClose() {
            var self = this;
            this.sidebarOpen = true;
            if (this.sidebarAutoCloseTimer) clearTimeout(this.sidebarAutoCloseTimer);
            this.sidebarAutoCloseTimer = setTimeout(function() {
                self.sidebarOpen = false;
            }, 5000);
        },
        toggleSection(key) {
            this.openSections[key] = !this.openSections[key];
        },
        filterORModels() {
            this.orCurrentPage = 1;
        },
        onORPageChange(page) {
            this.orCurrentPage = page;
        },
        selectORModel(modelId) {
            this.selectedORModel = modelId;
        },

        // --- Provider helpers ---
        getProvider(modelId) {
            if (!modelId) return "";
            var parts = modelId.split("/");
            return parts.length > 1 ? parts[0] : "";
        },
        getEnrichedProviderIcon(modelId) {
            var prefix = this.getProvider(modelId);
            var enriched = this.enrichedProviderMap[prefix];
            if (enriched && enriched.icon) return enriched.icon;
            return PROVIDER_ICON_MAP[prefix] || null;
        },
        getEnrichedProviderName(modelId) {
            var prefix = this.getProvider(modelId);
            var enriched = this.enrichedProviderMap[prefix];
            if (enriched) return enriched.displayName || prefix;
            return prefix;
        },
        onProviderIconError(e) {
            e.target.style.display = "none";
        },

        // --- Modality helpers ---
        getInputModalities(m) {
            try {
                return m.architecture && m.architecture.input_modalities ? m.architecture.input_modalities : ["text"];
            } catch (e) {
                return ["text"];
            }
        },
        getOutputModalities(m) {
            try {
                return m.architecture && m.architecture.output_modalities ? m.architecture.output_modalities : ["text"];
            } catch (e) {
                return ["text"];
            }
        },
        getKeyParams(m) {
            try {
                var params = m.supported_parameters || [];
                return params.filter(function(p) { return KEY_PARAMS.indexOf(p) !== -1; });
            } catch (e) {
                return [];
            }
        },
        modelSupportsParam(m, param) {
            try {
                var params = m.supported_parameters || [];
                return params.indexOf(param) !== -1;
            } catch (e) {
                return false;
            }
        },
        isRecommendedForChatbot(m) {
            var inputs = this.getInputModalities(m);
            return inputs.indexOf("text") !== -1 && inputs.indexOf("image") !== -1 && inputs.indexOf("file") !== -1;
        },
        getModalityTooltip(direction, mod) {
            var key = direction + "_" + mod;
            return MODALITY_TOOLTIPS[key] || mod;
        },
        getParamTooltip(param) {
            return PARAM_TOOLTIPS[param] || param;
        },

        // --- Format helpers ---
        formatContextLength(len) {
            if (!len) return "—";
            if (len >= 1000000) return (len / 1000000).toFixed(0) + "M";
            if (len >= 1000) return (len / 1000).toFixed(0) + "K";
            return String(len);
        },
        formatPrice(m, type) {
            try {
                var price = m.pricing && m.pricing[type] ? parseFloat(m.pricing[type]) : null;
                if (price === null || isNaN(price)) return "—";
                if (price === 0) return "Free";
                var perM = price * 1000000;
                if (this.isByokChannel) perM = perM * 1.1;
                return "$" + perM.toFixed(2);
            } catch (e) {
                return "—";
            }
        },
        orFallbackFilter(input, option) {
            return option.key.toLowerCase().indexOf(input.toLowerCase()) !== -1;
        },

        // --- Filter toggles ---
        toggleInputModality(val) {
            var idx = this.filterInputModalities.indexOf(val);
            if (idx !== -1) {
                this.filterInputModalities.splice(idx, 1);
            } else {
                this.filterInputModalities.push(val);
            }
            this.filterORModels();
        },
        toggleOutputModality(val) {
            var idx = this.filterOutputModalities.indexOf(val);
            if (idx !== -1) {
                this.filterOutputModalities.splice(idx, 1);
            } else {
                this.filterOutputModalities.push(val);
            }
            this.filterORModels();
        },
        toggleSupportedParam(val) {
            var idx = this.filterSupportedParams.indexOf(val);
            if (idx !== -1) {
                this.filterSupportedParams.splice(idx, 1);
            } else {
                this.filterSupportedParams.push(val);
            }
            this.filterORModels();
        },
        toggleProvider(slug) {
            var idx = this.filterProviders.indexOf(slug);
            if (idx !== -1) {
                this.filterProviders.splice(idx, 1);
            } else {
                this.filterProviders.push(slug);
            }
            this.filterORModels();
        },
        clearProviderFilter() {
            this.filterProviders = [];
            this.filterORModels();
        },
        clearAllFilters() {
            this.filterInputModalities = [];
            this.filterOutputModalities = [];
            this.filterSupportedParams = [];
            this.filterContextIdx = 0;
            this.filterPricingIdx = 0;
            this.filterProviders = [];
            this.orSearchQuery = "";
            this.filterORModels();
        },

        // --- Save ---
        async saveORModel() {
            if (!this.selectedORModel) {
                this.$message.warning("Lütfen bir LLM modeli seçin.");
                return;
            }
            try {
                this.saving = true;
                await CHATBOT_API.updateStorySingleKey({ id: this.storyId, key: "ai.llmModel", value: this.selectedORModel });
                await CHATBOT_API.updateStorySingleKey({ id: this.storyId, key: "ai.fallbackModels", value: this.selectedORFallbacks });
                await CHATBOT_API.updateStorySingleKey({ id: this.storyId, key: "ai.routingStrategy", value: this.orRoutingStrategy });
                this.$message.success("LLM modeli kaydedildi.");
            } catch (e) {
                this.$message.error("LLM modeli kaydedilemedi.");
            } finally {
                this.saving = false;
            }
        },
        async saveSelectedModel() {
            try {
                this.saving = true;
                const value = this.apiModelMap[this.selectedModel] || this.selectedModel;
                await CHATBOT_API.updateStorySingleKey({
                    id: this.storyId,
                    key: "ai.model",
                    value,
                });
                try {
                    const stories = (this.$store && this.$store.state && this.$store.state.CB && Array.isArray(this.$store.state.CB.stories))
                        ? this.$store.state.CB.stories
                        : [];
                    const idx = stories.findIndex((s) => s._id === this.storyId);
                    if (idx !== -1) {
                        const currentAi = stories[idx].ai || {};
                        this.$set(stories[idx], "ai", Object.assign({}, currentAi, { model: value }));
                    }
                } catch (e) {}
                try {
                    const cacheKey = `ai.selectedModel.${this.storyId}`;
                    if (typeof window !== "undefined") {
                        window.localStorage.setItem(cacheKey, value);
                    }
                } catch (e) {}
                this.$message.success("AI modeli güncellendi");
            } catch (e) {
                this.$message.error("AI modeli güncellenemedi");
            } finally {
                this.saving = false;
            }
        },
    },
    mounted() {
        this.loadStoryAISettings();
        if (typeof CHATBOT_API.getStoryById === "function") {
            CHATBOT_API.getStoryById(this.storyId)
                .then((res) => {
                    const story = (res && res.data) ? res.data : null;
                    const current = story && story.ai && story.ai.model ? story.ai.model : null;
                    const cacheKey = `ai.selectedModel.${this.storyId}`;
                    const cached = typeof window !== "undefined" ? window.localStorage.getItem(cacheKey) : null;
                    const resolved = this.normalizeModelName(current) || this.normalizeModelName(cached);
                    this.selectedModel = this.models.some((m) => m.id === resolved) ? resolved : this.selectedModel;
                })
                .catch(() => {
                    this.initSelectedModel();
                });
        } else {
            this.initSelectedModel();
        }
    },
    beforeDestroy() {
        if (this.sidebarAutoCloseTimer) {
            clearTimeout(this.sidebarAutoCloseTimer);
        }
    },
    watch: {
        currentStoryAi: {
            handler() {
                this.loadStoryAISettings();
            },
            deep: true,
        },
        storyId() {
            if (typeof CHATBOT_API.getStoryById === "function") {
                CHATBOT_API.getStoryById(this.storyId)
                    .then((res) => {
                        const story = (res && res.data) ? res.data : null;
                        const current = story && story.ai && story.ai.model ? story.ai.model : null;
                        const cacheKey = `ai.selectedModel.${this.storyId}`;
                        const cached = typeof window !== "undefined" ? window.localStorage.getItem(cacheKey) : null;
                        const resolved = this.normalizeModelName(current) || this.normalizeModelName(cached);
                        this.selectedModel = this.models.some((m) => m.id === resolved) ? resolved : "gpt-5.2";
                    })
                    .catch(() => {
                        this.initSelectedModel();
                    });
            } else {
                this.initSelectedModel();
            }
        },
    },
};
</script>
<style scoped lang="scss">
.ai-models {
    padding: 20px;

    .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;

        .title {
            font-size: 18px;
            font-weight: 600;
            margin: 0;
        }

        .actions {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .legend { display: flex; gap: 6px; }

        .control {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .control label {
            font-size: 13px;
            color: #555;
        }
    }

    .cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 16px;

        .card {
            background: #fff;
            border: 1px solid #e8e8e8;
            border-radius: 12px;
            padding: 16px;
            transition: transform .2s ease, box-shadow .2s ease;

            &:hover {
                transform: translateY(-3px);
                box-shadow: 0 8px 20px rgba(0,0,0,.08);
            }

            .card-head {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 6px;
            }

            .name { font-size: 16px; font-weight: 700; }
            .desc { color: #555; margin-bottom: 8px; }

            .md-icon {
                font-family: 'Material Icons';
                font-style: normal;
                font-weight: normal;
                font-size: 18px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                color: #333;
                margin-right: 6px;

                &.disabled { color: #bfbfbf; text-decoration: line-through; }
            }

            .details {
                margin-top: 12px;
                border-top: 1px dashed #eee;
                padding: 20px;

                .row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 6px 0;
                    font-size: 13px;
                    color: #333;
                }

                .block-title {
                    margin-top: 12px;
                    font-weight: 700;
                    font-size: 12px;
                    color: #555;
                    display: flex;
                    align-items: center;
                    gap: 6px;

                    .muted { font-weight: 400; color: #999; }
                }

                .p-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 6px 0;
                    font-size: 13px;
                }
            }
        }

        .card.is-selected {
            border-color: #1890ff;
            box-shadow: 0 8px 24px rgba(24,144,255,.15);
            position: relative;
            background: linear-gradient(180deg, #ffffff 0%, #f5fbff 100%);
        }

        .card .banner {
            height: 120px;
            border-radius: 10px;
            margin: 8px 0 12px;
            background: linear-gradient(135deg, #e5f0ff 0%, #fff 60%);
            border: 1px solid #f0f0f0;
            position: relative;
            overflow: hidden;
        }

        .card .banner .banner-title {
            position: absolute;
            left: 16px;
            bottom: 14px;
            color: #fff;
            font-weight: 800;
            font-size: 22px;
            line-height: 1;
            text-shadow: 0 2px 6px rgba(0,0,0,.35);
        }

        .model-gpt-5 .banner,
        .model-gpt-5-mini .banner,
        .model-gpt-5-nano .banner {
            background: radial-gradient(60% 90% at 20% 20%, #ffb2c8 0%, #ffc9a9 35%, #ffd9f2 60%, #f1e5ff 100%);
        }
        .model-gpt-51 .banner {
            background: radial-gradient(60% 90% at 20% 20%, #a78bfa 0%, #c4b5fd 35%, #ddd6fe 60%, #ede9fe 100%);
        }
        .model-gpt-52 .banner,
        .model-gpt-52-pro .banner {
            background: radial-gradient(60% 90% at 20% 20%, #34d399 0%, #6ee7b7 35%, #a7f3d0 60%, #d1fae5 100%);
        }
        .model-gpt-41 .banner {
            background: radial-gradient(60% 90% at 20% 20%, #7dd3fc 0%, #60a5fa 35%, #93c5fd 60%, #bfdbfe 100%);
        }

        .card .selected-ribbon {
            position: absolute;
            top: -10px;
            left: -10px;
            background: #1890ff;
            color: #fff;
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 4px 8px;
            border-radius: 999px;
            box-shadow: 0 6px 16px rgba(24,144,255,.25);
            font-size: 12px;
            z-index: 1;
        }
    }

    .badge { padding: 2px 8px; border-radius: 999px; font-size: 12px; border: 1px solid transparent; }
    .badge.best { background: #f6ffed; border-color: #b7eb8f; color: #135200; }
    .badge.cheap { background: #fff7e6; border-color: #ffd591; color: #874d00; }
    .badge.codex { background: #fef3c7; border-color: #f59e0b; color: #92400e; }
    .card.best { border-color: #b7eb8f; }
    .card.cheap { border-color: #ffd591; }
    .card.codex { border-color: #f59e0b; }

    .disclaimer { margin-top: 10px; font-size: 12px; color: #888; }

    // ===== OpenRouter / Supsis-RAG =====

    .or-page {
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    .or-topbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
        gap: 12px;
        flex-wrap: wrap;

        .or-topbar-left {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
        }

        .title { font-size: 18px; font-weight: 600; margin: 0; }

        .or-selected-badge {
            display: flex;
            align-items: center;
            gap: 6px;
            background: #e6f7ff;
            border: 1px solid #91d5ff;
            border-radius: 6px;
            padding: 4px 10px;
            font-size: 13px;

            .selected-label { color: #555; }

            .selected-model-name {
                color: #096dd9;
                font-family: monospace;
                font-size: 12px;
            }

            .tools-warn-inline {
                display: flex;
                align-items: center;
                gap: 2px;
                color: #d46b08;
                font-size: 12px;

                .material-icons { font-size: 14px; }
            }
        }
    }

    .or-routing-config {
        background: #fafafa;
        border: 1px solid #e5e5e5;
        border-radius: 10px;
        padding: 12px 16px;
        margin-bottom: 12px;

        .config-row {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;

            label {
                font-size: 13px;
                color: #555;
                white-space: nowrap;
                min-width: 80px;
            }
        }

        .inline-tools-warn {
            display: flex;
            align-items: center;
            gap: 4px;
            color: #d46b08;
            font-size: 12px;
            font-weight: 500;

            .material-icons { font-size: 14px; }
        }
    }

    // ===== Provider Strip =====
    .or-provider-strip {
        margin-bottom: 12px;
        background: #fafafa;
        border: 1px solid #f0f0f0;
        border-radius: 8px;
        padding: 8px 12px;

        .strip-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;

            .strip-label {
                font-size: 11px;
                font-weight: 700;
                color: #888;
                text-transform: uppercase;
                letter-spacing: 0.4px;
            }

            .strip-clear {
                font-size: 12px;
                color: #1890ff;
                cursor: pointer;

                &:hover { text-decoration: underline; }
            }
        }

        .strip-scroll-wrap {
            overflow-x: auto;
            overflow-y: hidden;
            // Scrollbar styling
            &::-webkit-scrollbar { height: 4px; }
            &::-webkit-scrollbar-track { background: #f5f5f5; border-radius: 2px; }
            &::-webkit-scrollbar-thumb { background: #d9d9d9; border-radius: 2px; }
        }

        .strip-scroll {
            display: flex;
            gap: 6px;
            padding-bottom: 4px;
            white-space: nowrap;
        }

        .strip-chip {
            display: inline-flex;
            flex-direction: column;
            align-items: center;
            gap: 3px;
            padding: 6px 10px;
            border: 1.5px solid #e8e8e8;
            border-radius: 8px;
            cursor: pointer;
            background: #fff;
            transition: all 0.15s;
            flex-shrink: 0;
            min-width: 58px;

            &:hover {
                border-color: #1890ff;
                background: #f0f7ff;
            }

            &.active {
                border-color: #1890ff;
                background: #e6f7ff;
                box-shadow: 0 0 0 2px rgba(24,144,255,.15);
            }

            .strip-icon {
                width: 20px;
                height: 20px;
                object-fit: contain;
                border-radius: 3px;
            }

            .strip-fallback {
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #f0f0f0;
                border-radius: 3px;
                font-size: 8px;
                font-weight: 700;
                color: #555;
            }

            .strip-name {
                font-size: 10px;
                color: #555;
                text-align: center;
                line-height: 1.2;
                max-width: 60px;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }

    .or-loading {
        padding: 60px;
        display: flex;
        justify-content: center;
    }

    // ===== Two-column layout =====
    .or-main-layout {
        display: flex;
        gap: 0;
        align-items: flex-start;
    }

    // ===== Sidebar =====
    .or-sidebar {
        width: 276px;
        min-width: 276px;
        overflow: hidden;
        position: sticky;
        top: 0;
        transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                    min-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                    opacity 0.25s ease;

        &.is-closed {
            width: 0 !important;
            min-width: 0 !important;
            opacity: 0;
        }
    }

    .or-sidebar-inner {
        width: 260px;
        max-height: calc(100vh - 260px);
        overflow-y: auto;
        overflow-x: hidden;
        padding-right: 16px;
        border-right: 1px solid #f0f0f0;

        // Scrollbar slim
        &::-webkit-scrollbar { width: 4px; }
        &::-webkit-scrollbar-track { background: transparent; }
        &::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 2px; }

        .sidebar-search {
            padding: 0 0 12px;
        }

        .sidebar-clear-btn {
            padding: 12px 0;
        }

        // Accordion sections
        .filter-section {
            border-bottom: 1px solid #f5f5f5;

            &:last-of-type { border-bottom: none; }
        }

        .filter-section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 0;
            cursor: pointer;
            user-select: none;

            &:hover .section-title-wrap span:first-of-type {
                color: #1890ff;
            }

            .section-title-wrap {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                font-weight: 700;
                color: #333;
                text-transform: uppercase;
                letter-spacing: 0.4px;
            }

            .section-icon {
                font-size: 15px;
                color: #888;
            }

            .section-badge {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                min-width: 16px;
                height: 16px;
                padding: 0 4px;
                background: #1890ff;
                color: #fff;
                border-radius: 8px;
                font-size: 10px;
                font-weight: 700;
                text-transform: none;
                letter-spacing: 0;
            }

            .section-chevron {
                font-size: 18px;
                color: #bbb;
                transition: transform 0.2s;

                &.open {
                    transform: rotate(180deg);
                }
            }
        }

        .filter-section-body {
            padding-bottom: 10px;
        }

        .section-desc {
            font-size: 11px;
            color: #aaa;
            margin-bottom: 8px;
            line-height: 1.4;
        }

        .modality-mandatory-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 6px;

            .mod-chip {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 3px 8px;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 500;
                background: #e6f7ff;
                border: 1px solid #91d5ff;
                color: #096dd9;
                cursor: default;

                .material-icons { font-size: 13px; }
            }

            .mandatory-tag {
                font-size: 10px;
                color: #ff4d4f;
                background: #fff1f0;
                border: 1px solid #ffa39e;
                border-radius: 4px;
                padding: 1px 5px;
                font-weight: 600;
            }
        }

        .modality-row {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 8px;
            border-radius: 6px;
            cursor: pointer;
            user-select: none;
            transition: background 0.15s;

            &:hover { background: #f5f5f5; }
            &.active { background: #e6f7ff; }
            &.row-tools { border-left: 2px solid #faad14; }

            .mod-row-icon { font-size: 16px; color: #888; width: 18px; }
            .mod-row-label { flex: 1; font-size: 13px; color: #333; }
            .mod-row-check { font-size: 15px; color: #1890ff; }

            .tools-required-dot {
                color: #ff4d4f;
                font-size: 10px;
                margin-left: 4px;
            }
        }

        .filter-hint {
            display: flex;
            align-items: flex-start;
            gap: 4px;
            font-size: 11px;
            color: #888;
            margin-top: 8px;
            line-height: 1.4;

            .material-icons { font-size: 13px; margin-top: 1px; color: #bbb; }

            &.tools-hint {
                color: #d46b08;
                .material-icons { color: #faad14; }
            }
        }

        .slider-wrap {
            padding: 8px 12px 20px;
        }
    }

    // ===== Model area =====
    .or-model-area {
        flex: 1;
        min-width: 0;
        padding-left: 16px;

        .or-model-area-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 12px;
            flex-wrap: wrap;
            gap: 8px;

            .model-area-left {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .model-count { font-size: 13px; color: #888; font-weight: 500; }

            .sidebar-toggle-btn {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 4px 10px;
                border: 1px solid #e5e5e5;
                border-radius: 6px;
                background: #fff;
                cursor: pointer;
                font-size: 12px;
                color: #555;
                transition: all 0.15s;
                position: relative;

                &:hover {
                    border-color: #1890ff;
                    color: #1890ff;
                    background: #f0f7ff;
                }

                .material-icons { font-size: 16px; }

                .active-filter-dot {
                    position: absolute;
                    top: 3px;
                    right: 3px;
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #1890ff;
                }
            }

            .recommendation-hint {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                color: #555;

                .rec-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #52c41a;
                    flex-shrink: 0;
                }
            }
        }
    }

    .or-cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
        gap: 12px;
        margin-bottom: 16px;
    }

    .or-card {
        background: #fff;
        border: 2px solid #e5e5e5;
        border-radius: 12px;
        padding: 14px;
        cursor: pointer;
        transition: all 0.2s;
        position: relative;

        &:hover { border-color: #1890ff; box-shadow: 0 4px 16px rgba(24,144,255,.12); }

        &.is-selected {
            border-color: #1890ff;
            background: linear-gradient(180deg, #fff 0%, #f0f8ff 100%);
        }

        &.is-fallback {
            border-color: #b5f5ec;
            background: #f6fffd;
        }

        &.is-recommended { border-color: #b7eb8f; &:hover { border-color: #52c41a; } }

        &.has-tools-issue { border-color: #ffd591; }

        .selected-ribbon {
            position: absolute;
            top: -1px;
            right: -1px;
            background: #1890ff;
            color: #fff;
            display: flex;
            align-items: center;
            gap: 3px;
            padding: 3px 8px;
            border-radius: 0 10px 0 8px;
            font-size: 11px;
            font-weight: 600;

            .material-icons { font-size: 13px; }

            &.fallback-ribbon { background: #13c2c2; }
        }

        .card-top-badge {
            display: inline-flex;
            align-items: center;
            gap: 3px;
            font-size: 10px;
            font-weight: 600;
            padding: 2px 7px;
            border-radius: 4px;
            margin-bottom: 8px;

            .material-icons { font-size: 12px; }

            &.recommended-badge { background: #f6ffed; color: #389e0d; border: 1px solid #b7eb8f; }
            &.tools-badge { background: #fff7e6; color: #d46b08; border: 1px solid #ffd591; }
        }

        .or-card-provider {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 6px;

            .or-prov-img { width: 18px; height: 18px; object-fit: contain; border-radius: 3px; }
            .or-prov-name { font-size: 11px; color: #888; font-weight: 500; }
        }

        .or-model-name { font-size: 14px; font-weight: 600; color: #222; margin-bottom: 2px; line-height: 1.3; }
        .or-model-id { font-family: monospace; font-size: 11px; color: #999; margin-bottom: 10px; word-break: break-all; }

        .or-modalities {
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-bottom: 8px;

            .mod-row-group {
                display: flex;
                align-items: center;
                gap: 4px;
                flex-wrap: wrap;
            }

            .mod-group-lbl {
                font-size: 10px;
                color: #aaa;
                font-weight: 600;
                width: 22px;
                text-align: right;
                flex-shrink: 0;
            }

            .mod-badge {
                font-size: 10px;
                padding: 1px 6px;
                border-radius: 3px;
                font-weight: 500;
                background: #f0f0f0;
                color: #555;
                border: 1px solid #e0e0e0;
                cursor: help;

                &.mod-text { background: #f0f5ff; color: #1d39c4; border-color: #d6e4ff; }
                &.mod-image { background: #fff0f6; color: #c41d7f; border-color: #ffadd2; }
                &.mod-file { background: #f9f0ff; color: #531dab; border-color: #d3adf7; }
                &.mod-audio { background: #fff7e6; color: #874d00; border-color: #ffd591; }
                &.mod-video { background: #e6fffb; color: #006d75; border-color: #87e8de; }
            }
        }

        .or-params-row {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            margin-bottom: 10px;

            .param-chip {
                font-size: 10px;
                padding: 1px 6px;
                border-radius: 3px;
                background: #f5f5f5;
                color: #666;
                border: 1px solid #e8e8e8;
                cursor: help;

                &.chip-tools { background: #e6f7ff; color: #096dd9; border-color: #91d5ff; font-weight: 600; }
                &.chip-tools-warn { background: #fff7e6; color: #d46b08; border-color: #ffd591; }
            }
        }

        .or-stats {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            border-top: 1px solid #f0f0f0;
            padding-top: 8px;

            .or-stat {
                display: flex;
                flex-direction: column;
                gap: 1px;
                cursor: help;

                .stat-lbl { font-size: 9px; color: #aaa; text-transform: uppercase; font-weight: 600; }
                .stat-val { font-size: 12px; color: #333; font-weight: 500; }
            }
        }
    }

    .or-pagination {
        display: flex;
        justify-content: center;
        margin-top: 8px;
    }

    .or-cards.auto-router-locked {
        opacity: 0.5;
        pointer-events: none;
    }

    .or-card.is-locked {
        cursor: not-allowed;
    }
}
</style>
